import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import cx from 'classnames';
import { ModalOverlayTransitionWrapper, ModalContentTransitionWrapper } from './modalTransitionWrapper.js';
import { buildModalContent } from './modalUtils.js';
const StyledModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  z-index: -50;
`;

const StyledModalContent = styled.div`
  display: none;
  position: fixed;
  z-index: 210;
  height: 85%;
  width: 100%;
  background: ${({ backgroundColor }) => backgroundColor};

  h1,h2,h3,h4,h5,p,a,li {
    color: ${props => props.textColor};
  }

  @media (min-width: 62em) {
    height: 100%;
    width: 50%;
    max-width: 800px;
    border-radius: ${({ transitionType }) => (transitionType !== 'slide_from_right' ? '0 10px 10px 0' : '10px 0 0 10px')};
  }

  .button_container {
    display: flex;
    flex-direction: row-reverse;
    position: sticky;
    background: ${({ backgroundColor }) => backgroundColor};
    height: 3.5rem;
    border-radius: 10px 10px 0 0;

    @media (min-width: 62em) {
      flex-direction: ${({ transitionType }) => (transitionType !== 'slide_from_left' ? 'row-reverse' : 'row')};
      border-radius: ${({ transitionType }) => (transitionType !== 'slide_from_left' ? '10px 0 0 0' : '0 10px 0 0')};
    }
  }

  .modal_close_button {
    font-size: 1.8rem;
    color: ${({ backgroundColor }) => backgroundColor};
    filter: invert(100%);
    cursor: pointer;
    border: none;
    background: transparent;
    margin: 0 1rem;
  }

  .content_container {
    padding: 0 2rem 2rem 2rem;
    height: calc(100% - 3.5rem);
    overflow-y: auto;

    @media (min-width: 62em) {
      * > .child_container {
        position: fixed;
        height: calc(100vh - 3.5rem);
        width: 50%;
        max-width: 800px;
        border-radius: ${({ transitionType }) => (transitionType === 'slide_from_left' ? '0 0 10px 0' : '0 0 0 10px')};
        overflow-y: auto;
      }
    }
  }
`;

const ModalContent = ({
  modalContent,
  isShowing,
  hide,
  withPadding,
  transitionType = 'pop',
  disallowCrawling,
  backgroundColor,
  textColor,
}) => {
  const [mainElement, setMainElement] = useState(null);
  const modalOverlayRef = useRef(null);

  const builtModalContent = useMemo(() => {
    return buildModalContent(modalContent);
  }, [modalContent, buildModalContent])

  useEffect(() => {
    //prevent page scrolling outside of modal
    isShowing ? (document.body.style.overflowY = 'hidden') : (document.body.style.overflowY = 'visible');
  }, [isShowing]);

  useEffect(() => {
    const mainElements = document.getElementsByTagName('main');
    setMainElement(mainElements[0]);
  }, []);

  const handleClickOutside = useCallback(
    (event) => {
      if (modalOverlayRef.current && isShowing && modalOverlayRef.current.contains(event.target)) {
        hide();
      }
    },
    [isShowing, hide]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside]);

  return mainElement
    ? ReactDOM.createPortal(
      <React.Fragment>
        <ModalOverlayTransitionWrapper>
          <CSSTransition
            in={isShowing}
            timeout={500}
            classNames="fade"
          >
            <StyledModalOverlay ref={modalOverlayRef} />
          </CSSTransition>
        </ModalOverlayTransitionWrapper>
        <ModalContentTransitionWrapper
          backgroundColor={backgroundColor}
        >
          <CSSTransition
            in={isShowing}
            timeout={transitionType === 'pop' ? 0 : 500}
            classNames={transitionType}
          >
            <StyledModalContent
              transitionType={transitionType}
              backgroundColor={backgroundColor}
              textColor={textColor}
            >
              {(!disallowCrawling || isShowing) && (
                <>
                  <div className="button_container">
                    <button
                      type="button"
                      className="modal_close_button"
                      data-dismiss="modal"
                      aria-label="Close"
                      onClick={hide}
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div className='content_container'>
                    {builtModalContent}  
                  </div>
                </>
              )}
            </StyledModalContent>
          </CSSTransition>
        </ModalContentTransitionWrapper>
      </React.Fragment>,
      mainElement
    )
    : null;
};
export default ModalContent;
